import { GopherGlyph } from './GopherGlyph';
import { useTranslation } from 'react-i18next';
import { TeeButton } from '@/components/ui/tee-button';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { TopBarIconButton } from '@/components/ui/top-bar-icon-button';
import { useAssistant } from '@/contexts/AssistantContext';
import { useIsMobile } from '@/hooks/useBreakpoint';
import { AssistantSpotlight } from '@/components/assistant/AssistantSpotlight';
import { cn } from '@/utils/cn';
import { shortcutLabel } from '@/utils/shortcut';

/** Top-bar entry point for the assistant panel (D-03). */
export function AssistantToggle() {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const {
    access,
    isOpen,
    togglePanel,
    showNewPill,
    spotlightVisible,
    dismissSpotlight,
    tryGopherFromSpotlight,
  } = useAssistant();

  if (!access.hasAccess) return null;
  const disabled = !access.enabled;
  const pill = Boolean(showNewPill) && access.enabled;
  const spotlight = Boolean(spotlightVisible) && access.enabled;
  const toggleLabel = pill ? t('assistant.panel.toggleNewAria') : t('assistant.panel.toggle');

  const control = isMobile ? (
    <span className="relative inline-flex">
      <TopBarIconButton
        icon={<GopherGlyph size={20} />}
        label={toggleLabel}
        active={isOpen}
        onClick={disabled ? () => undefined : togglePanel}
        className={cn('w-11 h-11', disabled && 'pointer-events-none opacity-50')}
      />
      {pill && (
        <span
          data-testid="gopher-new-dot"
          aria-hidden="true"
          className="pointer-events-none absolute right-2 top-2 h-2 w-2 rounded-full bg-[var(--color-gold-500)]"
        />
      )}
    </span>
  ) : (
    <TeeButton
      variant="ghost"
      size="s"
      text={t('assistant.panel.toggle')}
      leftIcon={<GopherGlyph size={16} />}
      rightIcon={
        pill ? (
          <span
            aria-hidden="true"
            className="rounded-[4px] bg-[var(--color-gold-500)] px-2 py-1 text-[10px] font-semibold leading-none tracking-[0.06em] text-[var(--color-forest)]"
          >
            {t('assistant.panel.newPill')}
          </span>
        ) : undefined
      }
      aria-label={pill ? toggleLabel : undefined}
      aria-pressed={isOpen}
      disabled={disabled}
      onClick={togglePanel}
      className={cn(
        // Cream-on-forest in every state; only the fill changes. The ghost
        // variant's light-surface hover (dark text) must not leak through.
        'h-8 rounded-[6px] border px-3 font-[var(--type-font-family-heading)] text-[13px] font-semibold',
        'transition-colors duration-150 ease-in-out disabled:cursor-default',
        'focus-visible:ring-2 focus-visible:ring-[var(--ring-on-forest)] focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--color-forest)]',
        isOpen
          ? 'border-[var(--background-page-body)] bg-[var(--background-page-body)] text-[var(--color-forest)] hover:bg-[var(--background-page-body)] hover:text-[var(--color-forest)] active:bg-[var(--background-page-body)]'
          : 'border-[var(--border-on-forest)] bg-transparent text-[var(--background-page-body)] hover:border-[var(--border-on-forest-hover)] hover:bg-[var(--background-on-forest-hover)] hover:text-[var(--background-page-body)] active:bg-[var(--background-on-forest-active)] active:text-[var(--background-page-body)]',
        disabled && 'opacity-60 disabled:text-[var(--background-page-body)]',
      )}
    />
  );

  if (spotlight) {
    return (
      <AssistantSpotlight
        open
        anchor={
          <span className="inline-flex rounded-[6px] outline outline-2 outline-offset-4 outline-[var(--color-gold-500)]">
            {control}
          </span>
        }
        onTryIt={tryGopherFromSpotlight}
        onDismiss={dismissSpotlight}
      />
    );
  }

  const tooltipText = disabled
    ? t('assistant.launcherDisabledTooltip')
    : isMobile
      ? null
      : t('assistant.panel.toggleTooltip', { shortcut: shortcutLabel });
  if (!tooltipText) return control;

  // A disabled button swallows pointer events, so the tooltip hangs off a
  // wrapper. Zero delay and a z-index above the top bar so it is never clipped.
  return (
    <TooltipProvider delayDuration={0}>
      <Tooltip>
        <TooltipTrigger asChild>
          <span tabIndex={disabled ? 0 : undefined} className="inline-flex">
            {control}
          </span>
        </TooltipTrigger>
        <TooltipContent
          side="bottom"
          align="end"
          sideOffset={10}
          className="z-[100] max-w-[240px] text-[13px] text-[var(--text-content-title)]"
        >
          {tooltipText}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
